/**
 * Login page — invite-code flow shared with kra-photo-journal.
 * Tokens issued here are accepted by both kra-photo-journal-worker and
 * kra-tools (same JWT_SECRET).
 */

import { useCallback, useEffect, useState } from 'react';
import { redeemInvite, validateInvite } from '../lib/api.js';

type Screen = 'enter-code' | 'enter-label';

interface LoginPageProps {
  onLogin: (
    token: string,
    name: string,
    extras?: { role?: string | null; staffId?: string | null },
  ) => void;
}

function readInviteFromUrl(): string | null {
  const m = window.location.pathname.match(/^\/invite\/([A-Za-z0-9]+)\/?$/);
  return m?.[1] ?? null;
}

export function LoginPage({ onLogin }: LoginPageProps) {
  const [screen, setScreen] = useState<Screen>('enter-code');
  const [code, setCode] = useState('');
  const [suggestedLabel, setSuggestedLabel] = useState('');
  const [deviceLabel, setDeviceLabel] = useState('');
  const [roleHint, setRoleHint] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const validateCode = useCallback(async (raw: string) => {
    const trimmed = raw.trim().toUpperCase();
    if (!trimmed) {
      setError('Nhập mã mời');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const data = await validateInvite(trimmed);
      if (!data.valid) {
        setError(data.error ?? 'Mã mời không hợp lệ');
        return;
      }
      setSuggestedLabel(data.label ?? '');
      setDeviceLabel(data.label ?? '');
      setRoleHint(data.role ?? null);
      setScreen('enter-label');
    } catch {
      setError('Không kết nối được máy chủ');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    const fromUrl = readInviteFromUrl();
    if (fromUrl) {
      setCode(fromUrl.toUpperCase());
      window.history.replaceState({}, '', '/');
      void validateCode(fromUrl);
    }
  }, [validateCode]);

  const submitCode = useCallback(() => void validateCode(code), [code, validateCode]);

  const submitLabel = useCallback(async () => {
    const label = deviceLabel.trim();
    if (label.length < 2) {
      setError('Tên thiết bị phải có ít nhất 2 ký tự');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const data = await redeemInvite(code.trim().toUpperCase(), label);
      if (!data.token) {
        setError(data.error ?? 'Không đăng ký được thiết bị');
        setBusy(false);
        return;
      }
      onLogin(data.token, data.name, { role: data.role ?? null, staffId: data.staffId ?? null });
    } catch {
      setError('Không kết nối được máy chủ');
      setBusy(false);
    }
  }, [code, deviceLabel, onLogin]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-4">
      <div className="w-full max-w-sm rounded-3xl bg-card border border-border p-8 shadow-2xl">
        <div className="text-center mb-7">
          <h1 className="text-white text-xl font-semibold">KRA Tools Admin</h1>
          <p className="text-muted text-sm mt-1">
            {screen === 'enter-code' ? 'Nhập mã mời để vào dashboard' : 'Đặt tên cho thiết bị này'}
          </p>
        </div>

        {screen === 'enter-code' && (
          <div className="space-y-4">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitCode();
              }}
              placeholder="VÍ DỤ: ABCD"
              autoFocus
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              className="w-full text-center text-2xl font-mono tracking-[0.3em] uppercase rounded-2xl bg-card2 border border-border px-4 py-4 text-white placeholder:text-muted/50 outline-none focus:border-accent transition-colors"
            />
            <button
              onClick={submitCode}
              disabled={busy || code.trim().length < 3}
              className="w-full rounded-2xl bg-white text-bg font-semibold py-3 disabled:opacity-30"
            >
              {busy ? 'Đang kiểm tra…' : 'Tiếp tục'}
            </button>
          </div>
        )}

        {screen === 'enter-label' && (
          <div className="space-y-4">
            {roleHint && (
              <div className="rounded-xl bg-card2 border border-border px-3 py-2 text-xs text-muted text-center">
                Vai trò: <span className="text-white font-medium">{roleHint}</span>
                {suggestedLabel && (
                  <>
                    <span className="mx-2 opacity-40">·</span>
                    Gợi ý: <span className="text-white font-medium">{suggestedLabel}</span>
                  </>
                )}
              </div>
            )}
            <input
              type="text"
              value={deviceLabel}
              onChange={(e) => setDeviceLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submitLabel();
              }}
              placeholder="iPhone Linh, iPad shop, …"
              autoFocus
              className="w-full rounded-2xl bg-card2 border border-border px-4 py-3 text-white placeholder:text-muted/50 outline-none focus:border-accent transition-colors"
            />
            <button
              onClick={() => void submitLabel()}
              disabled={busy || deviceLabel.trim().length < 2}
              className="w-full rounded-2xl bg-white text-bg font-semibold py-3 disabled:opacity-30"
            >
              {busy ? 'Đang đăng ký…' : 'Đăng nhập'}
            </button>
            <button
              onClick={() => {
                setScreen('enter-code');
                setError(null);
              }}
              className="w-full text-xs text-muted hover:text-white transition-colors py-1"
            >
              ‹ Đổi mã mời khác
            </button>
          </div>
        )}

        {error && <p className="mt-4 text-center text-sm text-red-400">{error}</p>}
      </div>
    </div>
  );
}
